import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchAuctionLiveState,
  fetchAuctionSettings,
  fetchLiveTeamOwnerView,
  fetchLiveAuctionLiveState,
  fetchLiveAuctionSettings,
  fetchTeamOwnerView,
  submitAuctionLiveBid,
  submitLiveAuctionLiveBid,
} from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import { getAuctionManagerPath as getCommunityAuctionManagerPath } from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const AUTO_RESOLVE_BID_MS = 30000;
const getAuctionProcessMode = (value) => (value === 'manual' ? 'manual' : 'auto');
const getOwnerViewFetcher = (isLiveOwnerBidding) => (isLiveOwnerBidding ? fetchLiveTeamOwnerView : fetchTeamOwnerView);

const TeamOwnerBiddingPage = () => {
  const { token } = useAuth();
  const location = useLocation();
  const { organizationId, communityId, clubId, auctionManagerId, tournamentId } = useParams();
  const isLiveOwnerBidding = location.pathname.includes('/live/owner-bidding');
  const isIndividualRoute = Boolean(tournamentId && !organizationId && !communityId && !clubId);

  const auctionManagementPath =
    (isIndividualRoute
      ? `/individual-tournament/${tournamentId}/auction`
      : auctionManagerId
        ? (communityId
          ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
          : clubId
            ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
            : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId))
        : null) || '/management';
  const [auctionSettings, setAuctionSettings] = useState(null);
  const [teamOwnerView, setTeamOwnerView] = useState(null);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamOwnerError, setTeamOwnerError] = useState('');
  const [playerSnapshot, setPlayerSnapshot] = useState(null);
  const [liveBid, setLiveBid] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [autoResolveMs, setAutoResolveMs] = useState(null);
  const [liveAuctionStatus, setLiveAuctionStatus] = useState('');
  const auctionProcessMode = getAuctionProcessMode(auctionSettings?.auctionProcess);
  const isManualProcess = auctionProcessMode === 'manual';
  const isBiddingCompleted = String(liveAuctionStatus || '').toLowerCase() === 'completed';

  const assignedTeam = teamOwnerView?.team || null;
  const teamPlayers = assignedTeam?.players || [];
  const soldPlayers = teamOwnerView?.soldPlayers || [];
  const minBid = Number(
    playerSnapshot?.minBid ?? playerSnapshot?.basePrice ?? auctionSettings?.baseBid ?? 0
  );
  const maxBid = Number(playerSnapshot?.maxBid ?? 0);
  const bidIncrement = Number(auctionSettings?.bidIncrement || 0) || 500;
  const selectedTeam = assignedTeam?.name || '';
  const currentLiveBidAmount =
    playerSnapshot &&
    liveBid &&
    (liveBid.playerId
      ? liveBid.playerId === playerSnapshot.playerId
      : liveBid.playerName === playerSnapshot.playerName)
      ? Number(liveBid.amount || 0)
      : 0;
  const formatSoldPrice = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `₹${parsed.toLocaleString()}` : '—';
  };
  const nextBidAmount = (() => {
    if (!playerSnapshot) return 0;
    const base = currentLiveBidAmount || minBid || 0;
    const nextValue = currentLiveBidAmount ? base + bidIncrement : base || bidIncrement;
    if (maxBid && nextValue > maxBid) {
      return maxBid;
    }
    return nextValue;
  })();
  const bidMatchesPlayer =
    playerSnapshot &&
    liveBid &&
    (liveBid.playerId
      ? liveBid.playerId === playerSnapshot.playerId
      : liveBid.playerName === playerSnapshot.playerName);
  const lastBidTeamIndex = Number.isInteger(Number(liveBid?.teamIndex)) ? Number(liveBid.teamIndex) : null;
  const selectedTeamIndex = Number.isInteger(Number(assignedTeam?.teamIndex)) ? Number(assignedTeam.teamIndex) : null;
  const isConsecutiveBidBlocked = Boolean(playerSnapshot && bidMatchesPlayer && selectedTeamIndex !== null && lastBidTeamIndex === selectedTeamIndex);

  useEffect(() => {
    if (!token || !tournamentId) return;

    const loadTeams = async () => {
      setIsLoadingTeams(true);
      setTeamOwnerError('');

      try {
        const [ownerResult, settingsResult] = await Promise.allSettled([
          getOwnerViewFetcher(isLiveOwnerBidding)(tournamentId, token),
          isLiveOwnerBidding
            ? fetchLiveAuctionSettings(tournamentId, token)
            : fetchAuctionSettings(tournamentId, token),
        ]);

        if (ownerResult.status === 'fulfilled') {
          setTeamOwnerView(ownerResult.value || null);
        } else {
          setTeamOwnerView(null);
          setTeamOwnerError(ownerResult.reason?.message || 'Unable to load team owner information.');
        }

        if (settingsResult.status === 'fulfilled') {
          setAuctionSettings(settingsResult.value.settings || null);
        } else {
          setAuctionSettings(null);
        }
      } finally {
        setIsLoadingTeams(false);
      }
    };

    loadTeams();
  }, [isLiveOwnerBidding, token, tournamentId]);

  useEffect(() => {
    if (!token || !tournamentId) return;

    let isMounted = true;

    const refreshTeamOwnerView = async () => {
      try {
        const ownerView = await getOwnerViewFetcher(isLiveOwnerBidding)(tournamentId, token);
        if (!isMounted) return;
        setTeamOwnerView(ownerView || null);
        setTeamOwnerError('');
      } catch (error) {
        if (!isMounted) return;
        setTeamOwnerError(error.message || 'Unable to load team owner information.');
      }
    };

    const intervalId = setInterval(refreshTeamOwnerView, 4000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [isLiveOwnerBidding, token, tournamentId]);

  useEffect(() => {
    if (!token || !tournamentId) return;

    let isMounted = true;

    const loadLiveState = async () => {
      try {
        const response = isLiveOwnerBidding
          ? await fetchLiveAuctionLiveState(tournamentId, token)
          : await fetchAuctionLiveState(tournamentId, token);
        if (!isMounted) return;
        setPlayerSnapshot(response.live?.currentPlayer || null);
        setLiveBid(response.live?.latestBid || null);
        setLiveAuctionStatus(response.live?.status || response.live?.phase || '');
        setAuctionSettings((previous) => {
          const nextProcess = getAuctionProcessMode(response.live?.auctionProcess);
          if (previous?.auctionProcess === nextProcess) return previous;
          return { ...(previous || {}), auctionProcess: nextProcess };
        });
      } catch (error) {
        if (!isMounted) return;
        setPlayerSnapshot(null);
        setLiveBid(null);
        setLiveAuctionStatus('');
      }
    };

    loadLiveState();
    const intervalId = setInterval(loadLiveState, 4000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [isLiveOwnerBidding, token, tournamentId]);

  useEffect(() => {
    if (!playerSnapshot || isManualProcess) {
      setAutoResolveMs(null);
      return;
    }

    const getLastActivityTime = () => {
      if (bidMatchesPlayer && liveBid?.createdAt) {
        return new Date(liveBid.createdAt).getTime();
      }

      if (playerSnapshot?.updatedAt) {
        return new Date(playerSnapshot.updatedAt).getTime();
      }

      return null;
    };

    const updateRemaining = () => {
      const lastActivity = getLastActivityTime();
      if (!lastActivity) {
        setAutoResolveMs(null);
        return;
      }

      const elapsedMs = Date.now() - lastActivity;
      const remaining = Math.max(AUTO_RESOLVE_BID_MS - elapsedMs, 0);
      setAutoResolveMs(remaining);
    };

    updateRemaining();
    const intervalId = setInterval(updateRemaining, 1000);

    return () => clearInterval(intervalId);
  }, [bidMatchesPlayer, isManualProcess, liveBid, playerSnapshot]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage('');

    if (!token || !tournamentId) {
      setStatusMessage('Unable to submit bid right now.');
      return;
    }

    if (!playerSnapshot) {
      setStatusMessage('Waiting for the auction manager to share a player.');
      return;
    }

    if (!selectedTeam) {
      setStatusMessage('Choose your team before placing a bid.');
      return;
    }

    if (isConsecutiveBidBlocked) {
      setStatusMessage('Wait for another team to place the next bid.');
      return;
    }

    const amount = Number(nextBidAmount);

    const payload = {
      teamName: selectedTeam,
      amount,
      playerId: playerSnapshot.playerId,
      playerName: playerSnapshot.playerName,
      createdAt: new Date().toISOString(),
    };

    const submitBid = isLiveOwnerBidding ? submitLiveAuctionLiveBid : submitAuctionLiveBid;

    submitBid(tournamentId, payload, token)
      .then((response) => {
        setLiveBid(response.live?.latestBid || payload);
        setStatusMessage('Bid submitted to the live auction feed.');
      })
      .catch((error) => {
        setStatusMessage(error.message || 'Unable to submit bid right now.');
      });
  };

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4 d-grid gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <span className="badge bg-primary-subtle text-primary-emphasis">Team owner</span>
          <h1 className="h4 mb-1">Live bidding room</h1>
          <p className="text-muted mb-0">
            Track the active player and place bids that instantly appear in the auction manager console.
          </p>
        </div>
        <Link to={auctionManagementPath} className="btn btn-outline-primary">
          Back to auction management
        </Link>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-3">
          <h2 className="h6 text-uppercase text-muted">Current player</h2>
          {teamOwnerError ? <div className="alert alert-warning mb-0">{teamOwnerError}</div> : null}
          {!teamOwnerError && !assignedTeam ? (
            <div className="border rounded p-3 bg-light">
              <p className="text-muted mb-0">Assign a team to place bids.</p>
            </div>
          ) : null}
          {assignedTeam && teamPlayers.length ? (
            <p className="text-muted small mb-0">Team roster: {teamPlayers.length} players.</p>
          ) : null}
          {isBiddingCompleted ? (
            <div className="border rounded p-3 bg-success-subtle text-success-emphasis">
              <p className="fw-semibold mb-1">Bidding completed</p>
              <p className="mb-0">All players have completed the bidding process.</p>
            </div>
          ) : playerSnapshot ? (
            <div className="border rounded p-3 bg-light d-grid gap-2">
              <div>
                <p className="mb-1 fw-semibold">{playerSnapshot.playerName}</p>
                <p className="text-muted mb-0">{playerSnapshot.role}</p>
              </div>
              <div className="d-flex justify-content-between text-muted small">
                <span>Base bid</span>
                <span>₹{minBid.toLocaleString()}</span>
              </div>
              <div className="d-flex justify-content-between text-muted small">
                <span>Current bid</span>
                <span>₹{Number(currentLiveBidAmount || minBid || 0).toLocaleString()}</span>
              </div>
              <div className="d-flex justify-content-between text-muted small">
                <span>Next bid</span>
                <span>₹{Number(nextBidAmount || 0).toLocaleString()}</span>
              </div>
              {!isManualProcess && autoResolveMs !== null ? (
                <div className="d-flex justify-content-between text-muted small">
                  <span>Auto-resolve timer</span>
                  <span>{Math.max(Math.ceil(autoResolveMs / 1000), 0)}s</span>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="border rounded p-3 bg-light">
              <p className="text-muted mb-0">
                {isBiddingCompleted ? 'Bidding completed.' : 'Waiting for the auction manager to start the live shuffle.'}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="d-flex flex-column flex-md-row gap-2 align-items-start">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isBiddingCompleted || !playerSnapshot || !selectedTeam || !nextBidAmount || isConsecutiveBidBlocked}
              >
                Place bid +₹{bidIncrement.toLocaleString()}
              </button>
              {isLoadingTeams && !selectedTeam ? (
                <span className="text-muted small">Loading team…</span>
              ) : null}
              {isConsecutiveBidBlocked ? (
                <span className="text-muted small">Waiting for another team to bid.</span>
              ) : null}
              {statusMessage ? (
                <span className="text-muted small">{statusMessage}</span>
              ) : null}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h2 className="h6 text-uppercase text-muted mb-0">Sold players</h2>
            <span className="badge text-bg-light">{soldPlayers.length} sold</span>
          </div>
          {soldPlayers.length ? (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">Player</th>
                    <th scope="col">Role</th>
                    <th scope="col">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {soldPlayers.map((player) => (
                    <tr key={player.id}>
                      <td>
                        <div className="fw-semibold">{player.fullName || 'Unnamed player'}</div>
                        {player.email ? <small className="text-muted">{player.email}</small> : null}
                      </td>
                      <td className="text-muted">{player.primaryRole || 'Player'}</td>
                      <td>{formatSoldPrice(player.soldPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0">No players sold to your team yet.</p>
          )}
        </div>
      </div>
    </div>
  
    </PageShell>
  );
};

export default TeamOwnerBiddingPage;
